/**
 * El bucle de sondeo, una sola vez.
 *
 * Habia tres copias con `delayAbortable`, `throwIfAborted`, `throwIfPastDeadline` e
 * `isAxios404` reescritos carácter por carácter (trends, influencer y agent). Listening
 * habria sido la cuarta.
 *
 * Lo que cambia entre trabajos es la cadencia, el presupuesto y qué cuenta como terminal;
 * el bucle no. Eso es exactamente lo que se parametriza aquí.
 */

import axios from 'axios';
import { HttpError } from '@/errors/HttpError';

/** `[hasta N sondeos, esperar M ms]`, en orden ascendente. */
export type PollLadder = readonly (readonly [maxPolls: number, ms: number])[];

export interface PollRetryOptions {
  retries: number;
  backoffMs: number;
  isRetryable?: (err: unknown) => boolean;
}

export interface PollJobOptions<T> {
  signal?: AbortSignal;
  startedAt?: number;
  maxWaitMs: number;
  ladder: PollLadder;
  tailMs: number;
  timeoutMessage: string;
  isTerminal: (res: T) => boolean;
  onProgress?: (res: T) => void;
  initialDelayMs?: number;
  pauseWhenHidden?: boolean;
  retryOnError?: PollRetryOptions;
}

export function delayAbortable(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal?.addEventListener('abort', onAbort);
  });
}

function whenVisible(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onChange);
      signal?.removeEventListener('abort', onAbort);
    };

    const onChange = () => {
      if (document.visibilityState === 'hidden') return;
      cleanup();
      resolve();
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    document.addEventListener('visibilitychange', onChange);
    signal?.addEventListener('abort', onAbort);
  });
}

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}

export function isAxios404(err: unknown): boolean {
  if (err instanceof HttpError) return err.response.status === 404;
  return axios.isAxiosError(err) && err.response?.status === 404;
}

export function intervalFor(pollCount: number, ladder: PollLadder, tailMs: number): number {
  for (const [maxPolls, ms] of ladder) {
    if (pollCount < maxPolls) return ms;
  }
  return tailMs;
}

function isPausedWhileHidden<T>(options: PollJobOptions<T>): boolean {
  return options.pauseWhenHidden === true && document.visibilityState === 'hidden';
}

function isTransientHttpError(err: unknown): boolean {
  if (err instanceof HttpError) return err.response.status === 0 || err.response.status >= 500;
  if (!axios.isAxiosError(err)) return false;
  return err.response === undefined || err.response.status >= 500;
}

async function fetchWithRetry<T>(
  fetchStatus: (signal?: AbortSignal) => Promise<T>,
  retry: PollRetryOptions,
  options: PollJobOptions<T>,
  startedAt: number
): Promise<T> {
  const isRetryable = retry.isRetryable ?? isTransientHttpError;
  for (let failures = 0; ; failures += 1) {
    try {
      return await fetchStatus(options.signal);
    } catch (err) {
      if (options.signal?.aborted || failures >= retry.retries || !isRetryable(err)) throw err;
      await delayAbortable(retry.backoffMs * 2 ** failures, options.signal);
      if (isPausedWhileHidden(options)) await whenVisible(options.signal);
      if (Date.now() - startedAt > options.maxWaitMs) {
        throw new Error(options.timeoutMessage);
      }
    }
  }
}

/**
 * Sondea hasta que `isTerminal` diga que sí, se agote el presupuesto o se aborte.
 *
 * No interpreta el resultado: quien llama decide qué es terminal y qué hacer después. Así
 * un fallo del trabajo (FAILED) es terminal igual que un éxito, y el que llama distingue.
 */
export async function pollUntilTerminal<T>(
  fetchStatus: (signal?: AbortSignal) => Promise<T>,
  options: PollJobOptions<T>
): Promise<T> {
  const startedAt = options.startedAt ?? Date.now();
  let pollCount = 0;

  if (options.initialDelayMs) await delayAbortable(options.initialDelayMs, options.signal);

  for (;;) {
    const resumed = isPausedWhileHidden(options);
    if (resumed) await whenVisible(options.signal);
    throwIfAborted(options.signal);
    const expired = Date.now() - startedAt > options.maxWaitMs;
    if (expired && !resumed) {
      throw new Error(options.timeoutMessage);
    }

    const res = options.retryOnError
      ? await fetchWithRetry(fetchStatus, options.retryOnError, options, startedAt)
      : await fetchStatus(options.signal);
    options.onProgress?.(res);

    if (options.isTerminal(res)) {
      return res;
    }
    if (expired) {
      throw new Error(options.timeoutMessage);
    }

    pollCount += 1;
    await delayAbortable(intervalFor(pollCount, options.ladder, options.tailMs), options.signal);
  }
}
